/**
 * CompactEntityCard — clickable card for subsidiary/sister/holding display.
 */

import { Badge } from "@/ui/primitives/badge";
import { Building2, Users, ChevronRight } from "lucide-react";
import type { CorporateEntityDetail } from "./EntityDetailsCard";

interface CompactEntityCardProps {
  entity: CorporateEntityDetail;
  onClick: () => void;
}

export function CompactEntityCard({ entity, onClick }: CompactEntityCardProps) {
  return (
    <button
      onClick={onClick}
      className="group w-full overflow-hidden rounded-xl border border-border bg-background p-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 p-2 dark:border-blue-800 dark:bg-blue-950/30">
          <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              {entity.entityName}
            </span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {entity.acn ? `ACN ${entity.acn}` : ""}
            {entity.acn && entity.abn ? " · " : ""}
            {entity.abn ? `ABN ${entity.abn}` : ""}
          </p>
          <div className="mt-2 flex items-center gap-3">
            {entity.directorsAndOfficers.length > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Users className="h-3 w-3" />
                {entity.directorsAndOfficers.length} director
                {entity.directorsAndOfficers.length !== 1 ? "s" : ""}
              </span>
            )}
            {entity.shareholders.length > 0 && (
              <Badge variant="outline" size="sm" className="tabular-nums">
                {entity.shareholders.length} shareholder
                {entity.shareholders.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
